import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslations } from '../context/TranslationContext';
import { Header } from '../components/Header';
import { useReveal } from '../hooks/useReveal';
import { getArticles, toArticleLang } from '../services/articles';
import { SeoHead } from '../components/SeoHead';
import { getArticlesListMeta, articlesListPath, homePath, buildBreadcrumbSchema } from '../services/seo';

const PLACEHOLDER_CARDS = [
  { id: 1, color: 'from-[#D4C5B0] to-[#B8A994]', span: false },
  { id: 2, color: 'from-[#C5CFC5] to-[#A8B8A8]', span: false },
  { id: 3, color: 'from-[#C5C8D4] to-[#A8ABB8]', span: false },
  { id: 4, color: 'from-[#D4D0C5] to-[#B8B4A9]', span: false },
  { id: 5, color: 'from-[#C5C5CF] to-[#A8A8B8]', span: false },
  { id: 6, color: 'from-[#CFD4C5] to-[#B3B8A8]', span: false },
];

const CARD_BANNER_COLORS = [
  'from-[#D4C5B0] to-[#B8A994]',
  'from-[#C5CFC5] to-[#A8B8A8]',
  'from-[#C5C8D4] to-[#A8ABB8]',
  'from-[#D4D0C5] to-[#B8B4A9]',
  'from-[#C5C5CF] to-[#A8A8B8]',
  'from-[#CFD4C5] to-[#B3B8A8]',
];

const Articles = () => {
  const { t, lang } = useTranslations();
  const articleLang = toArticleLang(lang);
  const mainRef = useRef<HTMLDivElement>(null);
  const strings = t.articlePage;
  const articles = getArticles(articleLang);
  const { title, description } = getArticlesListMeta(articleLang, t);

  useReveal(mainRef);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  return (
    <div className="min-h-screen bg-cream animate-fadeIn" ref={mainRef}>
      <SeoHead
        lang={articleLang}
        title={title}
        description={description}
        path={articlesListPath(articleLang)}
        alternates={{
          ro: articlesListPath('ro'),
          hu: articlesListPath('hu'),
          en: articlesListPath('en'),
        }}
        jsonLd={[
          buildBreadcrumbSchema([
            { name: t.page.navigation.home, path: homePath(articleLang) },
            { name: t.articles.title, path: articlesListPath(articleLang) },
          ]),
        ]}
      />
      <Header variant="page" />

      <div className="pt-40 pb-16 px-8 lg:px-16 max-w-7xl mx-auto">
        <div className="max-w-3xl mb-20" data-reveal>
          {/* "coming soon" only while this language genuinely has nothing yet */}
          {articles.length === 0 && <div className="eyebrow mb-6">{t.articles.comingSoon}</div>}
          <h1 className="display text-[48px] md:text-[64px] mb-6">{t.articles.title}</h1>
          <p className="text-mute text-base leading-[1.8] font-light max-w-xl">
            {t.articles.description}
          </p>
        </div>

        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {articles.map((article, index) => (
              <RouterLink
                key={article.slug}
                to={`/${lang}/articles/${article.slug}`}
                className="group block"
                data-reveal
                data-reveal-delay={index % 3 === 1 ? '100' : index % 3 === 2 ? '200' : undefined}
              >
                <div
                  className={`h-[50px] bg-gradient-to-r ${CARD_BANNER_COLORS[index % CARD_BANNER_COLORS.length]} mb-6`}
                />
                <div className="space-y-3">
                  <h3 className="display text-[24px] leading-[1.2] link-grow">{article.title}</h3>
                  <div className="text-[12px] tracking-[0.1em] uppercase text-mute">
                    {(strings.practiceAreas as Record<string, string>)[article.practiceArea] ?? article.practiceArea}{' '}
                    · {article.readTime} {strings.readTimeSuffix}
                  </div>
                  <p className="text-mute text-sm leading-[1.7] font-light">{article.description}</p>
                </div>
              </RouterLink>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
            {PLACEHOLDER_CARDS.map((card, index) => (
              <div
                key={card.id}
                className="group"
                data-reveal
                data-reveal-delay={index % 3 === 1 ? '100' : index % 3 === 2 ? '200' : undefined}
              >
                <div
                  className={`relative aspect-[4/3] bg-gradient-to-br ${card.color} mb-6 overflow-hidden`}
                >
                  <div className="absolute inset-0 backdrop-blur-sm bg-white/20 flex items-center justify-center">
                    <span className="eyebrow text-ink/40">{t.articles.comingSoon}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-3 bg-ink/[0.06] rounded-full w-24" />
                  <div className="h-5 bg-ink/[0.08] rounded-full w-4/5" />
                  <div className="space-y-2 pt-1">
                    <div className="h-3 bg-ink/[0.05] rounded-full w-full" />
                    <div className="h-3 bg-ink/[0.05] rounded-full w-3/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {articles.length === 0 && (
        <footer className="border-t border-hair py-16 bg-cream mt-16">
          <div className="max-w-7xl mx-auto px-8 lg:px-16 text-center">
            <p className="text-mute text-sm font-light">{t.articles.footerNote}</p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Articles;
